import type { FC, ReactNode } from 'react'
import type { NavItem } from '@/types'

import Link from 'next/link'
import { useRouter } from 'next/router'
import { useRef } from 'react'
import { ArrowUp, ArrowUpRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { footerBus } from '@/lib/footer-bus'
import { isMotionPaused, motionStore, useMotionPaused } from '@/lib/motion'
import { PrimaryButton } from '@/components/generals/primary-button'
import { FieldNotes } from '@/components/footer/field-notes'
import { FooterOffice } from '@/components/footer/footer-office'
import { FooterSkyline } from '@/components/footer/footer-skyline'
import { FooterWordmark } from '@/components/footer/footer-wordmark'
import { LiftEmail } from '@/components/footer/lift-email'
import { NorthScale } from '@/components/footer/north-scale'
import { SheetFrame } from '@/components/footer/sheet-frame'
import { SHEET_TOTAL, TitleBlock } from '@/components/footer/title-block'
import { StatusReadout } from '@/components/footer/status-readout'

import { CONTACT_ITEM, MENU_ITEMS, OFFICES } from '@/constants/navigation'
import { SOCIAL_LINKS, STUDIO_EMAIL } from '@/constants/studio'

const INDEX: Array<NavItem> = [...MENU_ITEMS, CONTACT_ITEM]

const OFFICE_BUILDINGS: Record<string, string> = {
    STO: 'a masonry courtyard building at Hornsgatan 64',
    LIS: 'a converted riverside warehouse at Rua da Junqueira 112',
    NYC: 'a cast-iron loft building at 210 Lafayette Street'
}

const easeInOutCubic = (t: number): number => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2)

/**
 * The page's place in the drawing set: its index position plus one, with Contact as the final sheet.
 */
const sheetFor = (pathname: string): { sheet: number, title: string, href: string | null } => {
    if (pathname === CONTACT_ITEM.href) return { sheet: SHEET_TOTAL, title: CONTACT_ITEM.label, href: CONTACT_ITEM.href }

    const index = INDEX.findIndex(item => item.href === '/' ? pathname === '/' : pathname === item.href || pathname.startsWith(`${item.href}/`))

    if (index >= 0) return { sheet: index + 1, title: INDEX[index].label, href: INDEX[index].href }

    return { sheet: 0, title: pathname === '/500' ? 'Instrument fault' : 'Zone not scanned', href: null }
}

/**
 * The footer on every page: SHEET 13 OF 13 of the drawing set. A contact band, a giant wordmark in
 * points, six framed columns (studio, three offices, index, elsewhere), the six buildings as a
 * skyline and a title block that tracks the page.
 */
export const SiteFooter: FC = (): ReactNode => {
    const router = useRouter()
    const motionPaused = useMotionPaused()
    const sweepRef = useRef<HTMLDivElement>(null)
    const current = sheetFor(router.pathname)

    const backToTop = (): void => {
        const calm = window.matchMedia('(prefers-reduced-motion: reduce)').matches || isMotionPaused()
        const focusMain = (): void => document.getElementById('main')?.focus({ preventScroll: true })
        const start = window.scrollY

        if (calm || start < 2) {
            window.scrollTo({ top: 0, behavior: 'auto' })
            focusMain()

            return
        }

        const duration = Math.min(2400, Math.max(1200, start / 2.4))
        const began = performance.now()

        let raf = 0

        const stop = (): void => {
            cancelAnimationFrame(raf)
            window.removeEventListener('wheel', stop)
            window.removeEventListener('touchstart', stop)
            window.removeEventListener('keydown', stop)
        }

        const frame = (now: number): void => {
            const t = Math.min(1, (now - began) / duration)

            window.scrollTo(0, Math.round(start * (1 - easeInOutCubic(t))))

            if (t < 1) {
                raf = requestAnimationFrame(frame)

                return
            }

            stop()
            focusMain()
        }

        footerBus.emit('lift')

        sweepRef.current?.animate(
            [
                { transform: `translate3d(0, ${window.innerHeight}px, 0)`, opacity: 1 },
                { transform: 'translate3d(0, -56px, 0)', opacity: 1, offset: 0.92 },
                { transform: 'translate3d(0, -56px, 0)', opacity: 0 }
            ],
            { duration: Math.min(duration, 1500), easing: 'cubic-bezier(0.65, 0, 0.35, 1)' }
        )

        window.addEventListener('wheel', stop, { passive: true })
        window.addEventListener('touchstart', stop, { passive: true })
        window.addEventListener('keydown', stop)
        raf = requestAnimationFrame(frame)
    }

    return (
        <footer aria-labelledby='footer-title' className='relative border-t border-hairline pb-20 pt-6 desk:pb-24 desk:pt-10'>
            <h2 id='footer-title' className='sr-only'>
                Sheet {SHEET_TOTAL} of {SHEET_TOTAL}: studio contact and index
            </h2>

            <div className='relative mx-gutter border border-hairline'>
                <SheetFrame />

                <div className='desk:pl-8 desk:pt-8'>
                    <div className='desk:border-l desk:border-t desk:border-hairline'>
                        {/* 1. Contact band */}
                        <div className='flex flex-col gap-8 px-4 pb-6 pt-8 desk:flex-row desk:items-end desk:justify-between desk:px-6 desk:pb-8 desk:pt-10'>
                            <div className='min-w-0'>
                                <p className='hud text-mute'>
                                    Write to the studio
                                </p>

                                <LiftEmail email={STUDIO_EMAIL} className='mt-3 max-w-full break-all font-heading text-[clamp(26px,4.2vw,64px)] font-medium leading-[1.05] tracking-[-0.025em]' />
                            </div>

                            <div className='flex-none'>
                                <PrimaryButton href='/contact'>
                                    Engage the studio
                                </PrimaryButton>
                            </div>
                        </div>

                        {/* 2. The wordmark */}
                        <div className='px-4 pb-6 desk:px-6 desk:pb-8'>
                            <FooterWordmark />
                        </div>

                        {/* 3. The sheet */}
                        <div className='grid grid-cols-2 border-t border-hairline desk:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,0.9fr)_minmax(0,1.15fr)]'>
                            <div className='col-span-2 flex flex-col justify-between gap-8 px-4 py-6 desk:col-span-1 desk:px-5 desk:py-6'>
                                <div>
                                    <p className='hud text-ink'>
                                        Fig. 13 / Studio / Contact
                                    </p>

                                    <p className='hud text-mute'>
                                        Architecture and building intelligence
                                    </p>
                                </div>

                                <NorthScale />

                                <a href={`mailto:${STUDIO_EMAIL}`} className='hud w-fit border-b border-ink/45 pb-1 text-ink transition-colors duration-300 hover:border-signal hover:text-signal'>
                                    {STUDIO_EMAIL}
                                </a>

                                <button
                                    type='button'
                                    onClick={backToTop}
                                    className='hud group inline-flex w-fit items-center gap-2 border border-ink/40 px-3 py-2 text-ink transition-colors duration-300 hover:border-ink hover:text-signal'
                                >
                                    <ArrowUp className='size-4 transition-transform duration-500 ease-instrument group-hover:-translate-y-0.5' strokeWidth={1.5} aria-hidden='true' />
                                    Back to top
                                </button>
                            </div>

                            {OFFICES.map((office, index) => (
                                <FooterOffice
                                    key={office.code}
                                    office={office}
                                    building={OFFICE_BUILDINGS[office.code] ?? office.city}
                                    className={cn('border-t border-hairline desk:border-l desk:border-t-0', index % 2 === 1 && 'border-l', index === OFFICES.length - 1 && index % 2 === 0 && 'col-span-2 desk:col-span-1')}
                                />
                            ))}

                            <nav aria-label='Footer index' className='col-span-2 flex flex-col border-t border-hairline px-4 py-6 desk:col-span-1 desk:border-l desk:border-t-0 desk:px-5'>
                                <p className='hud text-mute'>
                                    Index
                                </p>

                                <ul className='mt-3 flex flex-1 flex-col'>
                                    {INDEX.map(item => {
                                        const isCurrent = current.href === item.href

                                        return (
                                            <li key={item.href} className='flex min-h-8 flex-1 items-center border-t border-hairline first:border-t-0'>
                                                <Link
                                                    href={item.href}
                                                    aria-current={isCurrent ? 'page' : undefined}
                                                    className='footer-index hud group relative inline-flex items-center gap-3 text-ink transition-[transform,color] duration-300 ease-instrument hover:translate-x-2 hover:text-signal focus-visible:translate-x-2'
                                                >
                                                    <span className='absolute right-full top-1/2 mr-1 h-px w-0 bg-signal transition-[width] duration-300 ease-instrument group-hover:w-2.5 group-focus-visible:w-2.5' aria-hidden='true' />

                                                    <span className={cn('size-1.5 flex-none', isCurrent ? 'bg-signal' : 'bg-transparent')} aria-hidden='true' />

                                                    <span className='text-mute transition-colors duration-300 group-hover:text-signal'>
                                                        {item.index}
                                                    </span>

                                                    {item.label}
                                                </Link>
                                            </li>
                                        )
                                    })}
                                </ul>
                            </nav>

                            <div className='col-span-2 flex flex-col justify-between gap-8 border-t border-hairline px-4 py-6 desk:col-span-1 desk:border-l desk:border-t-0 desk:px-5'>
                                <div>
                                    <p className='hud text-mute'>
                                        Elsewhere
                                    </p>

                                    <ul className='mt-4 space-y-2'>
                                        {SOCIAL_LINKS.map(link => (
                                            <li key={link.href}>
                                                <a href={link.href} target='_blank' rel='noreferrer' className='hud group inline-flex items-center gap-2 text-ink transition-colors duration-300 hover:text-signal'>
                                                    {link.label}
                                                    <ArrowUpRight className='size-4 transition-transform duration-500 ease-instrument group-hover:-translate-y-0.5 group-hover:translate-x-0.5' strokeWidth={1.5} aria-hidden='true' />
                                                    <span className='sr-only'>
                                                        (opens in a new tab)
                                                    </span>
                                                </a>
                                            </li>
                                        ))}
                                    </ul>
                                </div>

                                <div className='border-t border-hairline pt-5'>
                                    <FieldNotes />
                                </div>

                                <div className='border-t border-hairline pt-5'>
                                    <StatusReadout />
                                </div>

                                <button
                                    type='button'
                                    onClick={() => motionStore.set(!motionPaused)}
                                    aria-pressed={motionPaused}
                                    className='hud inline-flex w-fit items-center gap-2.5 text-mute transition-colors duration-300 hover:text-signal'
                                >
                                    <span aria-hidden='true' className={motionPaused ? 'inline-block size-1.5 bg-signal' : 'inline-block size-1.5 bg-point'} />
                                    {motionPaused ? 'Motion paused' : 'Pause all motion'}
                                </button>
                            </div>
                        </div>

                        {/* 4. The skyline */}
                        <div className='border-t border-hairline px-4 pt-3 desk:px-6'>
                            <FooterSkyline />
                        </div>

                        {/* 5. The title block */}
                        <div className='border-t border-hairline'>
                            <p className='hud px-4 py-3 text-mute desk:px-6'>
                                Norrvik architects / Stockholm / Lisbon / 2009 to 2026
                            </p>

                            <div className='desk:mr-[240px] desk:border-r desk:border-hairline'>
                                <TitleBlock sheet={current.sheet} title={current.title} />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div ref={sweepRef} className='pointer-events-none fixed inset-x-0 top-0 z-[85] h-14 border-t border-signal bg-signal/[0.06] opacity-0' aria-hidden='true' />
        </footer>
    )
}
